import EventEmitter from 'eventemitter3';
import { DEFAULT_FETCH_POLICY, READ_CACHE_POLICIES } from './constants';
import { ApiCacheMissError } from './errors';
import { GenericCache } from './generic-cache';
import { createSubscription, genCacheUpdateEvent, getParamsId } from './lib';
import { ApiRequestManager } from './request-manager';
const ERROR_EVENT = 'error';
export class Api {
    constructor(params = {}) {
        this.responseBodyCache = new GenericCache();
        this.emitter = new EventEmitter();
        /**
         * Makes an API request
         */
        this.request = (params, options = {}) => {
            const { fetchPolicy = DEFAULT_FETCH_POLICY } = options;
            if (!READ_CACHE_POLICIES.includes(fetchPolicy)) {
                return this.requestManager.getResponseBody(params, options);
            }
            const cachedResponse = this.responseBodyCache.get(getParamsId(params));
            if (cachedResponse) {
                if (fetchPolicy === 'cache-and-fetch') {
                    // kick off in the background
                    // once it succeeds, the cache will be updated
                    // and subscribers will be notified
                    this.requestManager.getResponseBody(params, options).catch(() => {
                        // ignore - can be handled via `Api#onError`
                    });
                }
                return Promise.resolve(cachedResponse);
            }
            if (fetchPolicy === 'cache-only') {
                return Promise.reject(new ApiCacheMissError(`Cache miss: ${params.url}`));
            }
            return this.requestManager.getResponseBody(params, options);
        };
        /**
         * Returns `true` if a `GET` of `DELETE` request matches params
         */
        this.requestInProgress = (params) => {
            return this.requestManager.requestInProgress(params);
        };
        /**
         * Saves a response directly to the cache
         */
        this.writeCachedResponse = (params, responseBody) => {
            this.responseBodyCache.set(getParamsId(params), responseBody);
            this.emitter.emit(genCacheUpdateEvent(params), responseBody);
        };
        /**
         * Reads a response directly from the cache
         *
         * The main reason you would use this over `Api#request` with a cached
         * fetch policy is that this runs synchronously.
         *
         * Note that if there is a cache miss, it will return `undefined`
         */
        this.readCachedResponse = (params) => {
            return this.responseBodyCache.get(getParamsId(params));
        };
        /**
         * Subscribes to cache updates for a given param's response
         */
        this.onCacheUpdate = (params, listener) => {
            const event = genCacheUpdateEvent(params);
            return createSubscription(this.emitter, event, listener);
        };
        /**
         * Returns the base url concatenated with the provided path
         */
        this.buildUrl = (path) => {
            return `${this.requestManager.baseUrl}${path}`;
        };
        /**
         * Set default headers to be passed to all API requests.
         * Useful for setting an authentication token.
         *
         * @param key Header key
         * @param value Header value
         */
        this.setDefaultHeader = (key, value) => {
            this.requestManager.setDefaultHeader(key, value);
        };
        /**
         * Configuring an error handler to be called on error
         */
        this.onError = (listener) => {
            return createSubscription(this.emitter, ERROR_EVENT, listener);
        };
        this.requestManager = new ApiRequestManager(params);
        this.defaultFetchPolicy = params.defaultFetchPolicy || DEFAULT_FETCH_POLICY;
        this.requestManager.onReceivedResponseBody(this.writeCachedResponse);
        this.requestManager.onError((error) => this.emitter.emit(ERROR_EVENT, error));
    }
}
//# sourceMappingURL=index.js.map